import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Download, TrendingUp, DollarSign, Users, Target } from 'lucide-react';
import { KpiCard } from '@/components/KpiCard';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { getAnalyticsSummary, getAnalyticsTimeseries, getAnalyticsChannels, getAnalyticsCreatives, getExportUrl } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';

const CHANNEL_NAMES = {
  meta: 'Meta',
  google: 'Google',
  tiktok: 'TikTok',
  apple_search_ads: 'Apple Search Ads'
};

const fadeIn = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [channels, setChannels] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // If user is a client, filter by their client_id
      const params = {};
      if (user?.role === 'client' && user?.client_id) {
        params.client_id = user.client_id;
      }
      const [sumRes, tsRes, chRes, crRes] = await Promise.all([
        getAnalyticsSummary(params),
        getAnalyticsTimeseries(params),
        getAnalyticsChannels(params),
        getAnalyticsCreatives(params)
      ]);
      setSummary(sumRes.data);
      setTimeseries(tsRes.data);
      setChannels(chRes.data);
      setCreatives(crRes.data);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-sm text-slate-500">KPIs werden geladen...</div>
      </div>
    );
  }

  if (!summary || summary.total_installs === 0) {
    return <EmptyState title="Keine Daten" description="Noch keine Kampagnen-Daten vorhanden. Bitte wenden Sie sich an Ihr Agentur-Team." />;
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="Dashboard" description="Übersicht Ihrer Kampagnen-Performance">
        <Button onClick={() => {
          const params = {};
          if (user?.role === 'client' && user?.client_id) params.client_id = user.client_id;
          window.open(getExportUrl('analytics', params), '_blank');
        }} variant="outline" className="btn-lift" data-testid="dashboard-export-csv">
          <Download className="w-4 h-4 mr-2" /> CSV Export
        </Button>
      </PageHeader>
      
      {/* KPI Cards */}
      <motion.div variants={fadeIn} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <KpiCard label="Installs" value={summary.total_installs} prefix="" testId="kpi-card-installs" />
        <KpiCard label="CPI" value={summary.avg_cpi} prefix="€" testId="kpi-card-cpi" />
        <KpiCard label="Spend" value={summary.total_spend} prefix="€" testId="kpi-card-spend" />
        <KpiCard label="ROAS" value={summary.roas} suffix="x" testId="kpi-card-roas" />
      </motion.div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-12 gap-4 sm:gap-6 mb-6">
        {/* Time Series Chart */}
        <motion.div variants={fadeIn} className="md:col-span-8 rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
          <h3 className="font-display text-base font-semibold text-slate-900 mb-4">Installs & Spend (Zeitverlauf)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={timeseries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => v?.slice(5)} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
              <Area yAxisId="left" type="monotone" dataKey="installs" stroke="#2BAE9B" fill="#2BAE9B" fillOpacity={0.1} strokeWidth={2} name="Installs" />
              <Area yAxisId="right" type="monotone" dataKey="spend" stroke="#2A6FDB" fill="#2A6FDB" fillOpacity={0.05} strokeWidth={2} name="Spend (€)" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Channel Breakdown */}
        <motion.div variants={fadeIn} className="md:col-span-4 rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
          <h3 className="font-display text-base font-semibold text-slate-900 mb-4">Kanal-Verteilung</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={channels} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis dataKey="channel" type="category" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => CHANNEL_NAMES[v] || v} width={90} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
              <Bar dataKey="installs" fill="#2BAE9B" radius={[0, 4, 4, 0]} name="Installs" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Creative Performance Table */}
      <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]">
        <div className="p-4 sm:p-6 border-b border-slate-100">
          <h3 className="font-display text-base font-semibold text-slate-900">Creative Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="creatives-performance-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left p-3 pl-6 font-medium text-slate-500">Creative</th>
                <th className="text-right p-3 font-medium text-slate-500">Installs</th>
                <th className="text-right p-3 font-medium text-slate-500">Spend (€)</th>
                <th className="text-right p-3 font-medium text-slate-500">Revenue (€)</th>
                <th className="text-right p-3 font-medium text-slate-500">CPI (€)</th>
                <th className="text-right p-3 pr-6 font-medium text-slate-500">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {creatives.map((cr, i) => (
                <tr key={i} className="border-b border-slate-50 last:border-0 table-row-hover hover:bg-slate-50">
                  <td className="p-3 pl-6 font-medium text-slate-900">{cr.creative_id}</td>
                  <td className="p-3 text-right tabular-nums text-slate-700">{cr.installs?.toLocaleString('de-DE')}</td>
                  <td className="p-3 text-right tabular-nums text-slate-700">{cr.spend?.toLocaleString('de-DE', {minimumFractionDigits: 2})}</td>
                  <td className="p-3 text-right tabular-nums text-slate-700">{cr.revenue?.toLocaleString('de-DE', {minimumFractionDigits: 2})}</td>
                  <td className="p-3 text-right tabular-nums text-slate-700">{cr.cpi?.toFixed(2)}</td>
                  <td className="p-3 pr-6 text-right">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      cr.roas >= 1.5 ? 'bg-emerald-50 text-emerald-700' : cr.roas >= 1 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {cr.roas?.toFixed(2)}x
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );
}
